import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountBalance, SideDrawer } from 'vopay';
import TransferForm from './TransferForm';
import TransferFormActions from './TransferFormActions';
import useTransferForm from './useTransferForm';

type TransferDrawerProps = {
  isOpen: boolean;
  /** Preselects the From account (a row's Transfer button); absent = start blank. */
  fromAccount?: AccountBalance;
  onClose: () => void;
  /** After a completed transfer; the host refreshes its list and closes the drawer. */
  onTransferred: () => void;
};

/**
 * The transfer flow in a side drawer over the Accounts page (spec §2). Counts the opens: a new key
 * remounts the drawer, and so the flow, fresh on each one, while the previous form stays through
 * the close animation instead of vanishing from the sliding-out card.
 */
export default function TransferDrawer(props: TransferDrawerProps) {
  const [numOpens, setNumOpens] = useState(0);
  const [wasOpen, setWasOpen] = useState(false);

  // Counted during render, not in an effect, so the previous instance never renders open (and
  // fetches) before the new key replaces it.
  if (props.isOpen !== wasOpen) {
    setWasOpen(props.isOpen);
    if (props.isOpen) setNumOpens((count) => count + 1);
  }

  return <TransferDrawerContent key={numOpens} {...props} />;
}

function TransferDrawerContent({ isOpen, fromAccount, onClose, onTransferred }: TransferDrawerProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'PartnerAccountTransfer' });
  const flow = useTransferForm({ isActive: isOpen, initialFromAccount: fromAccount, onTransferred });

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={t('drawerTitle', { defaultValue: 'Transfer Between Accounts' })}
      footer={<TransferFormActions flow={flow} fullWidth />}
    >
      <TransferForm isReviewing={flow.isReviewing} onSubmit={flow.ctx.handleSubmit}>
        {flow.renderFormElements()}
      </TransferForm>
    </SideDrawer>
  );
}
